import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, Plus, Filter, ArrowUpDown, Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { ProjectCreationModal } from '@/components/project-creation-modal';
import { ColumnConfiguratorModal, DEFAULT_COLUMNS, type ColumnDefinition } from '@/components/column-configurator-modal';
import { PaginationControls } from '@/components/pagination-controls';
import { fetchProjects, fetchClients, fetchEmployees, fetchTasks, fetchMilestones, fetchResourceAllocations } from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
  calculateDaysRemaining,
  calculateGrossProfit,
  calculateForecastAtCompletion,
  formatProjectType,
  formatRelativeTime,
  getDaysRemainingColor,
  getProjectTypeColor,
  getWIPColor,
} from '@/lib/utils';
import type { Project, Client, Employee, ProjectStatus, ProjectHealth, Task, Milestone, ResourceAllocation } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';
import { useDisplayDensity } from '@/hooks/use-display-density';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { usePagination } from '@/hooks/use-pagination';
import { useKeyboardPagination } from '@/hooks/use-keyboard-pagination';
import { ROUTES } from '@/routes';

interface ProjectsPageProps {
  onNavigateToProject?: (projectId: string) => void;
}

export function ProjectsPage({ onNavigateToProject }: ProjectsPageProps = {}) {
  const navigate = useNavigate();
  const density = useDisplayDensity();
  const { hasMinimizedForm, formState, acknowledgeModalOpen } = useProjectForm();
  const { preferences } = useUserPreferences();
  const tableRef = useRef<HTMLDivElement>(null);
  const { handleApiError } = useErrorHandler({ useToast: true });
  
  // Pagination (don't persist to URL to keep URLs clean)
  const {
    currentPage,
    pageSize,
    paginationParams,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });
  
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [healthFilter, setHealthFilter] = useState<ProjectHealth | 'all'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'health' | 'value' | 'completion'>('health');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnDefinition[]>(DEFAULT_COLUMNS);

  // Load reference data once (clients, employees, etc.)
  useEffect(() => {
    async function loadReferenceData() {
      try {
        const [clientsData, employeesData, tasksData, milestonesData, allocationsData] = await Promise.all([
          fetchClients('projects'),
          fetchEmployees('projects'),
          fetchTasks('projects'),
          fetchMilestones('projects'),
          fetchResourceAllocations('projects'),
        ]);
        setClients(clientsData);
        setEmployees(employeesData);
        setTasks(tasksData);
        setMilestones(milestonesData);
        setResourceAllocations(allocationsData);
      } catch (error) {
        handleApiError(error, '/api/reference-data', 'GET');
      }
    }
    loadReferenceData();
  }, [handleApiError]);

  // Open modal when form should be restored
  useEffect(() => {
    if (formState?.shouldOpenModal) {
      setCreateModalOpen(true);
      acknowledgeModalOpen();
    }
  }, [formState?.shouldOpenModal, acknowledgeModalOpen]);

  // Apply client-side filtering and sorting
  const allProjects = useMemo(() => {
    // In a real app, this would come from the backend
    // For now, we need to fetch all projects to filter/sort client-side
    return projects;
  }, [projects]);

  const filteredAndSortedProjects = useMemo(() => {
    return allProjects
      .filter(p => {
        const matchesSearch =
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.clientNames?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.clientIds?.split(',').some(id => clients.find(c => c.id === id)?.name.toLowerCase().includes(searchTerm.toLowerCase())));
        const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
        const matchesHealth = healthFilter === 'all' || p.health === healthFilter;
        return matchesSearch && matchesStatus && matchesHealth;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'health':
            const healthOrder = { red: 0, amber: 1, green: 2 };
            return healthOrder[a.health] - healthOrder[b.health];
          case 'value':
            return b.budgetRevenue - a.budgetRevenue;
          case 'completion':
            return a.completionPercent - b.completionPercent;
          default:
            return 0;
        }
      });
  }, [allProjects, searchTerm, statusFilter, healthFilter, sortBy, clients]);

  // Function to load/refresh projects
  const loadProjects = async () => {
    // Show loading state for initial load, transition state for page changes
    if (loading) {
      setLoading(true);
    } else {
      setIsTransitioning(true);
    }

    try {
      // Fetch all projects first (in real app, backend would handle filtering/sorting)
      const allProjectsData = await fetchProjects('projects');
      setProjects(allProjectsData);
      setLoading(false);
    } catch (error) {
      handleApiError(error, '/api/projects', 'GET');
      setLoading(false);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, [handleApiError]); // Only load once

  // Update pagination metadata when filtered data changes
  useEffect(() => {
    const totalItems = filteredAndSortedProjects.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
    const startIndex = (safePage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);

    setPagination({
      currentPage: safePage,
      pageSize,
      totalItems,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > 1,
      startIndex,
      endIndex,
    });

    // If current page is beyond total pages, reset to page 1
    if (currentPage > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [filteredAndSortedProjects.length, currentPage, pageSize, setPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    resetPagination();
  }, [searchTerm, statusFilter, healthFilter, resetPagination]);

  // Scroll to top when page changes
  useEffect(() => {
    if (tableRef.current && !loading) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage, loading]);

  // Keyboard shortcuts for pagination
  useKeyboardPagination({
    currentPage,
    totalPages: pagination.totalPages,
    onPageChange: setPage,
    enabled: !loading && !createModalOpen && !columnConfigOpen,
  });

  // Get current page of projects
  const paginatedProjects = useMemo(() => {
    const { startIndex, endIndex } = pagination;
    return filteredAndSortedProjects.slice(startIndex, endIndex);
  }, [filteredAndSortedProjects, pagination]);

  // Get client names - now supports multiple clients via clientNames field
  const getClientNames = (project: Project) => {
    // If project has clientNames populated (from stored procedure), use it directly
    if (project.clientNames) {
      return project.clientNames;
    }
    // Fallback: look up client names from clientIds
    if (project.clientIds) {
      const ids = project.clientIds.split(',').filter(Boolean);
      const names = ids.map(id => clients.find(c => c.id === id)?.name || 'Unknown');
      return names.join(', ');
    }
    return 'Unknown';
  };

  const getManagerName = (managerId: string) => {
    return employees.find(e => e.id === managerId)?.name || 'Unknown';
  };

  const handleSaveColumns = (newColumns: ColumnDefinition[]) => {
    setColumns(newColumns);
  };

  // Get visible columns sorted by order
  const visibleColumns = columns
    .filter(col => col.visible)
    .sort((a, b) => a.order - b.order);

  // Helper functions for calculated fields
  const getOverdueTasks = (projectId: string): number => {
    const now = new Date();
    return tasks.filter(
      t => t.projectId === projectId && 
      t.status !== 'completed' && 
      new Date(t.dueDate) < now
    ).length;
  };

  const getOverdueMilestones = (projectId: string): number => {
    return milestones.filter(
      m => m.projectId === projectId && m.status === 'overdue'
    ).length;
  };

  const getNextMilestone = (projectId: string): Milestone | undefined => {
    const now = new Date();
    const upcoming = milestones
      .filter(m => m.projectId === projectId && m.status === 'pending')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    return upcoming[0];
  };

  const getAllocatedResources = (projectId: string): number => {
    const uniqueEmployees = new Set(
      resourceAllocations
        .filter(a => a.projectId === projectId)
        .map(a => a.employeeId)
    );
    return uniqueEmployees.size;
  };

  const getResourceConflicts = (projectId: string): number => {
    return resourceAllocations.filter(
      a => a.projectId === projectId && a.conflictLevel !== 'none'
    ).length;
  };

  // Render column value based on column ID
  const renderColumnValue = (column: ColumnDefinition, project: Project) => {
    const cellClass = column.align === 'right' ? `${density.cell} text-right` : density.cell;

    switch (column.id) {
      case 'project-name':
        return (
          <TableCell key={column.id} className={density.cell}>
            <div>
              <div className="font-medium">{project.name}</div>
              <div className="text-xs text-muted-foreground">
                {formatDate(project.startDate)} - {formatDate(project.endDate)}
              </div>
            </div>
          </TableCell>
        );

      case 'client':
        return (
          <TableCell key={column.id} className={density.cell}>
            {getClientNames(project)}
          </TableCell>
        );

      case 'project-manager':
        return (
          <TableCell key={column.id} className={density.cell}>
            {getManagerName(project.projectManagerId)}
          </TableCell>
        );

      case 'project-status':
        return (
          <TableCell key={column.id} className={density.cell}>
            <Badge variant="outline" className={getStatusColor(project.status)}>
              {formatStatus(project.status)}
            </Badge>
          </TableCell>
        );

      case 'health-indicator':
        return (
          <TableCell key={column.id} className={density.cell}>
            <Badge className={getHealthBadgeColor(project.health)}>
              {project.health.toUpperCase()}
            </Badge>
          </TableCell>
        );

      case 'budget':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
          </TableCell>
        );

      case 'actual-cost':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>
              {formatCurrency(project.actualLabour + project.actualExpense)}
            </SensitiveData>
          </TableCell>
        );

      case 'revenue':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
          </TableCell>
        );

      case 'wip-amount':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>
              <span className={getWIPColor(project.wipAmount)}>
                {formatCurrency(project.wipAmount)}
              </span>
            </SensitiveData>
          </TableCell>
        );

      case 'gross-profit':
        const grossProfit = calculateGrossProfit(
          project.actualRevenue,
          project.actualLabour,
          project.actualExpense
        );
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>
              <span className={grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(grossProfit)}
              </span>
            </SensitiveData>
          </TableCell>
        );

      case 'forecast-at-completion':
        const eac = calculateForecastAtCompletion(
          project.actualLabour,
          project.actualExpense,
          project.budgetLabour,
          project.budgetExpense,
          project.completionPercent
        );
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatCurrency(eac)}</SensitiveData>
          </TableCell>
        );

      case 'allocated-hours':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{project.budgetHours.toLocaleString()} hrs</SensitiveData>
          </TableCell>
        );

      case 'variance':
        return (
          <TableCell key={column.id} className={cellClass}>
            <span className={getVarianceColor(project.variancePercent)}>
              {project.variancePercent > 0 ? '+' : ''}
              {formatPercent(project.variancePercent)}
            </span>
          </TableCell>
        );

      case 'completion-percentage':
        return (
          <TableCell key={column.id} className={density.cell}>
            <div className="flex items-center gap-2">
              <Progress value={project.completionPercent} className="h-2 w-20" />
              <span className="text-xs text-muted-foreground w-10 text-right">
                {formatPercent(project.completionPercent, 0)}
              </span>
            </div>
          </TableCell>
        );

      case 'margin':
        return (
          <TableCell key={column.id} className={cellClass}>
            <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
          </TableCell>
        );

      case 'project-type':
        return (
          <TableCell key={column.id} className={density.cell}>
            <Badge variant="outline" className={getProjectTypeColor(project.type)}>
              {formatProjectType(project.type)}
            </Badge>
          </TableCell>
        );

      case 'phase':
        return (
          <TableCell key={column.id} className={density.cell}>
            <span className="text-muted-foreground text-sm">-</span>
          </TableCell>
        );

      case 'start-date':
        return (
          <TableCell key={column.id} className={density.cell}>
            {formatDate(project.startDate)}
          </TableCell>
        );

      case 'end-date':
        return (
          <TableCell key={column.id} className={density.cell}>
            {formatDate(project.endDate)}
          </TableCell>
        );

      case 'days-remaining':
        const daysRemaining = calculateDaysRemaining(project.endDate);
        return (
          <TableCell key={column.id} className={cellClass}>
            <span className={getDaysRemainingColor(daysRemaining)}>
              {daysRemaining > 0 ? `${daysRemaining} days` : daysRemaining === 0 ? 'Today' : 'Overdue'}
            </span>
          </TableCell>
        );

      case 'next-milestone':
        const nextMilestone = getNextMilestone(project.id);
        return (
          <TableCell key={column.id} className={density.cell}>
            {nextMilestone ? (
              <div>
                <div className="font-medium text-sm">{nextMilestone.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(nextMilestone.dueDate)}
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">-</span>
            )}
          </TableCell>
        );

      case 'team-members':
        const teamCount = getAllocatedResources(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {teamCount > 0 ? `${teamCount} member${teamCount !== 1 ? 's' : ''}` : '-'}
          </TableCell>
        );

      case 'resource-conflicts':
        const conflicts = getResourceConflicts(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {conflicts > 0 ? (
              <Badge variant="destructive">{conflicts}</Badge>
            ) : (
              <span className="text-muted-foreground text-sm">None</span>
            )}
          </TableCell>
        );

      case 'tasks-overdue':
        const overdueTasks = getOverdueTasks(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {overdueTasks > 0 ? (
              <Badge variant="destructive">{overdueTasks}</Badge>
            ) : (
              <span className="text-green-600 text-sm">0</span>
            )}
          </TableCell>
        );

      case 'overdue-milestones':
        const overdueMilestones = getOverdueMilestones(project.id);
        return (
          <TableCell key={column.id} className={cellClass}>
            {overdueMilestones > 0 ? (
              <Badge variant="destructive">{overdueMilestones}</Badge>
            ) : (
              <span className="text-green-600 text-sm">0</span>
            )}
          </TableCell>
        );

      case 'last-updated':
        return (
          <TableCell key={column.id} className={density.cell}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-sm">{formatRelativeTime(project.updatedAt)}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {formatDate(project.updatedAt)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );

      default:
        return (
          <TableCell key={column.id} className={density.cell}>
            -
          </TableCell>
        );
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Projects" 
        description="Manage and monitor all your projects"
        actions={
          <>
            <Button 
              variant="outline" 
              size={density.buttonSize}
              onClick={() => setColumnConfigOpen(true)}
            >
              <Settings2 className={`mr-2 ${density.icon.sm}`} />
              Configure Columns
            </Button>
            <Button variant="outline" size={density.buttonSize}>
              <Filter className={`mr-2 ${density.icon.sm}`} />
              Advanced Filters
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button 
                      size={density.buttonSize} 
                      onClick={() => setCreateModalOpen(true)}
                      disabled={hasMinimizedForm}
                    >
                      <Plus className={`mr-2 ${density.icon.sm}`} />
                      New Project
                    </Button>
                  </div>
                </TooltipTrigger>
                {hasMinimizedForm && (
                  <TooltipContent>
                    <p>Complete or cancel the draft project in the sidebar first</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </>
        }
      />

      {/* Data Sensitivity Toggle moved to header bar */}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search projects or clients..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ProjectStatus | 'all')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="not-started">Not Started</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={healthFilter} onValueChange={(v) => setHealthFilter(v as ProjectHealth | 'all')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Health" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Health</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="amber">Amber</SelectItem>
                <SelectItem value="red">Red</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="health">Health (Worst First)</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="value">Value (High-Low)</SelectItem>
                <SelectItem value="completion">Completion</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card ref={tableRef}>
        <CardHeader>
          <CardTitle>
            {pagination.totalItems} Project{pagination.totalItems !== 1 ? 's' : ''}
            {(searchTerm || statusFilter !== 'all' || healthFilter !== 'all') && 
              ` (filtered from ${projects.length} total)`
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={isTransitioning ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
            <Table>
              <TableHeader>
                <TableRow className={density.row}>
                  {visibleColumns.map(column => (
                    <TableHead 
                      key={column.id} 
                      className={column.align === 'right' ? `${density.header} text-right` : density.header}
                    >
                      {column.label || column.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  // Skeleton loading state
                  Array.from({ length: pageSize }).map((_, i) => (
                    <TableRow key={i} className={density.row}>
                      {visibleColumns.map(column => (
                        <TableCell key={column.id} className={density.cell}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : pagination.totalItems === 0 ? (
                  <TableRow className={density.row}>
                    <TableCell colSpan={visibleColumns.length} className={`${density.cell} text-center text-muted-foreground py-12`}>
                      {searchTerm || statusFilter !== 'all' || healthFilter !== 'all' ? (
                        <div className="space-y-2">
                          <p className="text-lg font-medium">No projects found</p>
                          <p className="text-sm">Try adjusting your filters or search term</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-lg font-medium">No projects yet</p>
                          <p className="text-sm">Get started by creating your first project</p>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProjects.map(project => (
                    <TableRow 
                      key={project.id} 
                      className={`${density.row} cursor-pointer hover:bg-accent`}
                      onClick={() => {
                        if (onNavigateToProject) {
                          onNavigateToProject(project.id);
                        } else {
                          navigate(ROUTES.PROJECT_DETAILS(project.id));
                        }
                      }}
                    >
                      {visibleColumns.map(column => renderColumnValue(column, project))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {!loading && pagination.totalItems > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              showPageSizeSelector={true}
              className="pt-4 border-t"
            />
          )}
        </CardContent>
      </Card>

      {/* Project Creation Modal */}
      <ProjectCreationModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        mode="create"
        workflowStyle={preferences.projectCreationWorkflow}
        onProjectCreated={loadProjects}
      />

      {/* Column Configurator Modal */}
      <ColumnConfiguratorModal
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={handleSaveColumns}
      />
    </div>
  );
}
